import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import Field from './Field';
import style from '../../../../scss/components/form.module.scss';
import {t} from "i18next";

const normalizeEans = (rawEan) => {
    if (!rawEan) return [];
    if (Array.isArray(rawEan)) {
        return rawEan
            .map((item) => {
                if (!item) return null;
                if (typeof item === 'string') return item.trim();
                if (typeof item === 'object' && item.eanCode) return String(item.eanCode).trim();
                if (typeof item === 'object' && item.ean) return String(item.ean).trim();
                return null;
            })
            .filter(Boolean);
    }

    return String(rawEan)
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
};

const uniq = (arr) => Array.from(new Set(arr));

const EanCodesInput = ({entity, onChange, resource, name = 'eans', ...props}) => {
    const current = useMemo(() => normalizeEans(entity?.[name]), [entity, name]);
    const [draft, setDraft] = useState('');

    const commit = (list) => {
        onChange({[name]: list});
    };

    const addCode = (raw) => {
        const value = String(raw || draft).trim();
        if (!value) return;
        const next = uniq([...current, value]);
        commit(next);
        setDraft('');
    };

    const removeAt = (index) => {
        const next = current.filter((_, position) => position !== index);
        commit(next);
    };

    const replaceAt = (index, value) => {
        const position = String(value).trim();
        const next = current.slice();
        next[index] = position;
        commit(uniq(next).filter(Boolean));
    };

    const onDraftKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            addCode();
        }
    };

    return (
        <div className={style.formGroup}>
            <Field name={name} resource={resource} {...props}>
                {(hasError, attributes) => {
                    const {id, placeholder, ...attr} = attributes;

                    return (
                        <div {...attr}>
                            <div className="flex flex-wrap gap-4 mb-2">
                                {current.length === 0 && (
                                    <span className="text-gray-500">{t('products.no_ean_added')}</span>
                                )}
                                {current.map((code, idx) => {
                                    return (
                                        <div
                                            key={`${code}-${idx}`}
                                            className={`inline-flex items-center gap-2 w-auto px-2 py-1`}
                                        >
                                            <input
                                                className={` bg-transparent outline-none text-sm w-auto ${style.textArea}`}
                                                value={code}
                                                onChange={(event) => replaceAt(idx, event.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="text-red-600 hover:text-red-800"
                                                onClick={() => removeAt(idx)}
                                                title={t('products.remove_ean')}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    id={id}
                                    className={`${style.textArea} ${hasError ? style.isInvalid : ''} flex-1`}
                                    value={draft}
                                    onChange={(event) => setDraft(event.target.value)}
                                    onKeyDown={onDraftKeyDown}
                                    placeholder={placeholder}
                                />
                                <button
                                    type="button"
                                    className="px-3 py-1 rounded bg-light-yoghurt"
                                    onClick={() => addCode()}
                                    disabled={!draft.trim()}
                                >
                                    Add
                                </button>
                            </div>

                            <p className="text-sm text-gray-500 mt-1">
                                {t('products.add_ean_rules')}
                            </p>
                        </div>
                    );
                }}
            </Field>
        </div>
    );
};

EanCodesInput.propTypes = {
    onChange: PropTypes.func.isRequired,
    entity: PropTypes.object.isRequired,
    resource: PropTypes.string.isRequired,
    name: PropTypes.string,
};

export default EanCodesInput;
